import { Formik, Form } from 'formik';
import { Stack, Typography } from '@mui/material';
import Button from '~/components/common/Button';
import { Field } from '~/components/common/Formik';
import { InviteUserSchemaWith2FA } from '~/utils/schema/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '~/providers/AuthProvider';
import {
  handleLoginSuccess,
  useLogin,
  useLogout,
  useRegister,
} from '~/services/api/auth';
import { fromQueryString } from '~/utils/queryString';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetInvitationQuery,
  useValidateInvitationMutation,
} from '~/services/api/shared';
import { showAlert, alertParams } from '~/components/common/Alert';
import { queryKeys } from '~/services/api/queryKeys';
import { queryClient } from '~/lib/react-query';
import { firebaseGetToken } from '~/services/firebase';
import { jwtDecode } from 'jwt-decode';
import { BroadcastEvents, channel } from '~/lib/broadcast';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { use2Fa } from '~/hooks/use2Fa';
import { memoryStorage } from '~/utils/localStorage';
import { AuthLayout } from './components/AuthLayout';
import { StyledAuthTitle } from './style';
import VerifyCode2FA from './VerifyCode2FA';
import DialogVerifyCode2FA from './DialogVerifyCode2FA';

type InviteUserProps = {};

export const InviteUser: React.FC<InviteUserProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { account } = useAuth();
  const isNewSSO = useFeatureIsOn(
    'acd-11071-cookie-attributes-not-set-optimal-security',
  );

  const [storeAuthParams, setStoreAuthParams] = useState({
    email: '',
    password: '',
    invitation_code: '',
  });
  const [errorMsg, setErrorMsg] = useState<string>('');
  const loggedWithDiffAcc = useRef(false);

  const { mutate: logoutMutation } = useLogout({
    onSuccess: () => {
      window.location.reload();
    },
  });

  const code = useMemo(
    () => fromQueryString(location.search)?.code as string,
    [location.search],
  );

  const { data: invitationInfo, isFetching: isFetchingInvitation } =
    useGetInvitationQuery({ params: { code } });

  const invitationAlertParams = {
    expired: {
      description: 'Your link has expired',
      okText: 'Close',
    },
    notExist: {
      description: 'Invitation does not exist.',
      okText: 'Close',
    },
    differentAccount: {
      description: 'Please login with your account to accept the invitation.',
      okText: 'Close',
      onOk: () => navigate(`/login?code=${code}`),
      onCancel: () => navigate(`/login?code=${code}`),
    },
    wasAccepted: {
      description: 'Invitation was accepted.',
      okText: 'Close',
    },
    inValid: {
      description: 'Invitation link was invalid.',
      okText: 'Close',
    },
    invitationAcceptSuccess: {
      ...alertParams.success,
      description: 'Accept invitation successfully.',
      okText: 'OK',
    },
  };

  const { mutate: validateInvitation, isLoading: isLoadingValidateInvitation } =
    useValidateInvitationMutation({
      onSuccess: async () => {
        memoryStorage.setOrgId(null);
        navigate('/invite-user/success');
        channel.postMessage({
          type: BroadcastEvents.RefreshOtherPageWhenInvited,
        });
        queryClient.refetchQueries([queryKeys.settings.account]);
      },
      onError: () => {
        queryClient.refetchQueries([queryKeys.settings.account]);
      },
    });

  const {
    handleEnable2FA,
    handleSendCode,
    loadingSendCode,
    phone,
    handleVerifyCode,
    loadingVerify,
    enable2FA,
    handleCancel,
    errorMsg: errorMsg2Fa,
    clearErrorMsg,
  } = use2Fa({
    recaptchaId: 'recaptcha-container-id',
    onLoginSuccess: async () => {
      await handleLoginSuccess(isNewSSO, {
        isInvalidateQueries: false,
        isSetCustomClaim: true,
      });
      validateInvitation({ code });
    },
  });

  const { mutate: loginMutation, isLoading: isLoadingLogin } = useLogin({
    options: {
      isInvalidateQueries: false,
      isSetCustomClaim: true,
    },
    onSuccess: async () => {
      validateInvitation({ code });
    },
    onError: (errorString, error) => {
      if (error?.code === 'auth/multi-factor-auth-required') {
        handleEnable2FA(error);
      } else setErrorMsg(errorString);
    },
  });

  const { mutate: registerMutation, isLoading: isLoadingRegister } =
    useRegister({
      onSuccess: async () => {
        loginMutation(storeAuthParams);
      },
    });

  const hasAccountInOtherPlatform =
    invitationInfo?.exists_in_firebase && !invitationInfo?.has_app;

  useEffect(() => {
    if (!code) {
      showAlert({
        ...alertParams.warning,
        description: 'Missing invitation code!',
        okText: 'Close',
        onOk: () => navigate('/login'),
        onCancel: () => navigate('/login'),
      });
    }
  }, [code]);

  useEffect(() => {
    const handleVerifyInvitation = async () => {
      let alertParamsData = null;
      if (isFetchingInvitation) return;
      if (code && invitationInfo) {
        if (invitationInfo.expired) {
          alertParamsData = invitationAlertParams.expired;
          showAlert({
            ...alertParams.warning,
            onOk: () => navigate('/login'),
            onCancel: () => navigate('/login'),
            ...alertParamsData,
          });
          return;
        }

        if (!invitationInfo.email) {
          alertParamsData = invitationAlertParams.inValid;
          showAlert({
            ...alertParams.warning,
            onOk: () => navigate('/login'),
            onCancel: () => navigate('/login'),
            ...alertParamsData,
          });
          return;
        }

        if (invitationInfo.is_accepted) {
          alertParamsData = invitationAlertParams.wasAccepted;
          showAlert({
            ...alertParams.warning,
            onOk: () => navigate('/login'),
            onCancel: () => navigate('/login'),
            ...alertParamsData,
          });
          return;
        }

        const idToken = await firebaseGetToken();
        const tkDecoded = idToken
          ? jwtDecode<{ email: string }>(idToken)
          : { email: null };

        const isLogged = !!idToken;
        const hasCFAccount =
          invitationInfo.exists_in_firebase && invitationInfo.has_app;
        const isSameAccount =
          tkDecoded.email?.toLowerCase() ===
          invitationInfo?.email?.toLowerCase();

        if (hasCFAccount) {
          if (isLogged) {
            if (isSameAccount) {
              validateInvitation({ code });
            } else {
              loggedWithDiffAcc.current = true;
              logoutMutation();
            }
          } else {
            alertParamsData = invitationAlertParams.differentAccount;
            // continuing create account
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (isLogged) {
            logoutMutation();
          }
        }
      } else {
        alertParamsData = invitationAlertParams.notExist;
      }

      if (alertParamsData && !loggedWithDiffAcc.current) {
        showAlert({
          ...alertParams.warning,
          onOk: () => navigate('/login'),
          onCancel: () => navigate('/login'),
          ...alertParamsData,
        });
      }
    };
    handleVerifyInvitation();
  }, [code, account, invitationInfo, isFetchingInvitation]);

  const clearAllErrorMsg = () => {
    clearErrorMsg();
    setErrorMsg('');
  };

  const handleSubmit = ({ email, password }) => {
    clearAllErrorMsg();
    setStoreAuthParams({ email, password, invitation_code: code });
    if (hasAccountInOtherPlatform) {
      return loginMutation({ email, password });
    }
    return registerMutation({ email, password, invitation_code: code });
  };

  return (
    <AuthLayout>
      <Formik
        initialValues={{
          full_name: invitationInfo?.display_name || '',
          email: invitationInfo?.email || '',
          password: '',
        }}
        enableReinitialize
        validationSchema={InviteUserSchemaWith2FA}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            <Stack spacing={2}>
              <StyledAuthTitle>Create account</StyledAuthTitle>
              {hasAccountInOtherPlatform && (
                <Typography
                  sx={{
                    background: '#FDE9AB',
                    p: 1,
                    textAlign: 'center',
                    fontSize: 16,
                    fontFamily: 'Lato',
                  }}
                >
                  Breadstack Delivery is integrated with the Breadstack Account
                  system. It looks like you’ve got a Breadstack Account already,
                  please enter your password to continue.
                </Typography>
              )}

              <Field.TextInput name='full_name' legend='Full Name' disabled />
              <Field.UnifiedTextField
                name='email'
                label='Email'
                disabled
                onChange={() => clearAllErrorMsg()}
              />
              <Field.UnifiedTextField
                name='password'
                label='Password'
                type='password'
                onChange={() => clearAllErrorMsg()}
              />
              <Typography
                sx={{
                  lineHeight: 1.66,
                  color: '#F24822',
                  mt: '10px !important',
                  fontSize: 14,
                }}
              >
                {errorMsg || errorMsg2Fa}
              </Typography>
              <Button
                type='submit'
                loading={
                  isLoadingLogin ||
                  isLoadingRegister ||
                  isLoadingValidateInvitation
                }
              >
                {hasAccountInOtherPlatform
                  ? 'Login to Accept Invitation'
                  : 'Create Account'}
              </Button>
            </Stack>
          </Form>
        )}
      </Formik>
      {enable2FA && (
        <DialogVerifyCode2FA open={enable2FA} onClose={handleCancel}>
          <VerifyCode2FA
            phone={phone}
            onComplete={handleVerifyCode}
            onClickReSend={handleSendCode}
            loading={loadingSendCode || loadingVerify}
            error={errorMsg2Fa}
            clearError={clearErrorMsg}
          />
        </DialogVerifyCode2FA>
      )}
      <div id='recaptcha-container-id' />
    </AuthLayout>
  );
};
